import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaEnvelope } from '@react-icons/all-files/fa/FaEnvelope';
import { FaExclamationCircle } from '@react-icons/all-files/fa/FaExclamationCircle';
import { FaUserCircle } from '@react-icons/all-files/fa/FaUserCircle';
import ProfaseModal from './ProfaseModal';
import {
  fetchUsuarioMail,
  refreshUsuarioMailStatus,
  UsuarioMailStatus,
  usuarioMailErrorMessage,
  validarUsuarioMail,
} from '../shared/usuario-mail';

export interface AccountModalProps {
  open: boolean;
  onClose: () => void;
  userId?: number | string;
  login: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

function AccountUserAvatar({ userId, size = 64 }: { userId?: number | string; size?: number }) {
  const [errored, setErrored] = useState(false);
  if (!errored && userId) {
    return (
      <img
        src={`/public/usuario/${userId}/image.png`}
        alt="avatar"
        width={size}
        height={size}
        onError={() => setErrored(true)}
        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return <FaUserCircle className="account-modal__avatar-fallback" style={{ fontSize: size }} />;
}

export default function AccountModal({
  open,
  onClose,
  userId,
  login,
  firstName,
  lastName,
  email,
}: AccountModalProps) {
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || login || 'Usuario';
  const accountEmail = email?.trim() ?? '';

  const [mailStatus, setMailStatus] = useState<UsuarioMailStatus | null>(null);
  const [loadingMail, setLoadingMail] = useState(false);
  const [validating, setValidating] = useState(false);
  const [mailError, setMailError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadMailStatus = useCallback(async () => {
    setLoadingMail(true);
    setMailError(null);
    setSuccessMessage(null);
    try {
      let mail = await fetchUsuarioMail();
      if (mail?.puedeEnviar) {
        setMailStatus(mail);
        return;
      }
      if (mail?.email) {
        try {
          mail = await refreshUsuarioMailStatus();
        } catch {
          // Sin config usable en SES; mantener lo que haya en local.
        }
      }
      setMailStatus(mail);
    } catch (err) {
      setMailStatus(null);
      setMailError(usuarioMailErrorMessage(err));
    } finally {
      setLoadingMail(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadMailStatus();
    } else {
      setMailStatus(null);
      setMailError(null);
      setSuccessMessage(null);
      setLoadingMail(false);
      setValidating(false);
    }
  }, [open, loadMailStatus]);

  const handleVerify = async () => {
    if (!accountEmail) return;
    setValidating(true);
    setMailError(null);
    setSuccessMessage(null);
    try {
      const updated = await validarUsuarioMail(accountEmail, displayName !== login ? displayName : undefined);
      setMailStatus(updated);
      if (updated.puedeEnviar) {
        setSuccessMessage('Mail verificado correctamente.');
      } else {
        setSuccessMessage('Revisá tu correo y hacé clic en el enlace de verificación.');
      }
    } catch (err) {
      setMailError(usuarioMailErrorMessage(err));
    } finally {
      setValidating(false);
    }
  };

  const mailUi = useMemo(() => {
    if (loadingMail) {
      return { message: 'Cargando estado del mail…', showVerify: false, showResend: false, verified: false };
    }
    if (mailStatus?.puedeEnviar) {
      return {
        message: 'Tu correo está verificado. Podés enviar mails desde las actividades.',
        showVerify: false,
        showResend: false,
        verified: true,
      };
    }
    if (mailStatus?.verificationStatus === 'Pending') {
      return {
        message: 'Te enviamos un correo. Revisá tu bandeja e inbox de spam.',
        showVerify: false,
        showResend: true,
        verified: false,
      };
    }
    if (
      mailStatus?.verificationStatus === 'Failed'
      || mailStatus?.verificationStatus === 'TemporaryFailure'
    ) {
      return {
        message: 'No se pudo verificar. Pedí un nuevo enlace.',
        showVerify: true,
        showResend: false,
        verified: false,
      };
    }
    return {
      message: 'Validá tu correo para enviar mails.',
      showVerify: true,
      showResend: false,
      verified: false,
    };
  }, [loadingMail, mailStatus]);

  const verifyDisabled = !accountEmail || validating || loadingMail;
  const verifyLabel = mailUi.showResend ? 'Reenviar' : 'Verificar mail';

  return (
    <ProfaseModal
      open={open}
      titleId="account-modal-title"
      size="compact"
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !validating) onClose();
      }}
    >
      <div className="account-modal__header">
        <h2 id="account-modal-title" className="profase-modal-title m-0">
          <FaUserCircle style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Cuenta
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onClose}
          disabled={validating}
        >
          Cerrar
        </button>
      </div>

      <div className="account-modal__body">
        <div className="account-modal__profile-row">
          <AccountUserAvatar userId={userId} size={64} />
          <div className="account-modal__profile-info">
            <div className="account-modal__display-name">{displayName}</div>
            <div className="account-modal__meta-row">
              <span className="account-modal__meta-label">Login</span>
              {login || '—'}
            </div>
            <div className="account-modal__meta-row">
              <span className="account-modal__meta-label">Email</span>
              {accountEmail || '—'}
            </div>
          </div>
        </div>

        <div className="account-modal__mail-section">
          <div className="account-modal__section-title">
            <FaEnvelope style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Mail para envíos
          </div>

          {mailUi.verified && (
            <div className="account-modal__alert account-modal__alert--verified">
              <FaCheckCircle style={{ flexShrink: 0 }} />
              <span>Mail verificado</span>
            </div>
          )}

          <p className="account-modal__mail-message">{mailUi.message}</p>

          {!accountEmail && (
            <div className="account-modal__alert account-modal__alert--warning">
              <FaExclamationCircle style={{ flexShrink: 0 }} />
              <span>No hay email en la cuenta.</span>
            </div>
          )}

          {mailError && (
            <div className="account-modal__alert account-modal__alert--error">{mailError}</div>
          )}

          {successMessage && (
            <div className="account-modal__alert account-modal__alert--success">{successMessage}</div>
          )}

          {(mailUi.showVerify || mailUi.showResend) && (
            <button
              type="button"
              className="profase-form-btn-primary btn-sm"
              style={{ marginTop: 8 }}
              onClick={handleVerify}
              disabled={verifyDisabled}
            >
              {validating ? 'Enviando…' : verifyLabel}
            </button>
          )}
        </div>
      </div>
    </ProfaseModal>
  );
}
